import { useEffect, useState } from 'react';
import { X, Wrench, Loader2, CheckCircle2, XCircle, ShieldOff } from 'lucide-react';

/**
 * Tool-execution-stack visibility (the "which tools is it calling now, and what ran"
 * ask) — a small badge under a message that opens the full ordered trace sequence on
 * click. Backed by app_agent_traces via chatEngine.js's startTrace()/finishTrace(),
 * which now write a 'running' row the moment a call starts, not just after it
 * finishes, so the live case (still-pending turn) and the historical case (a
 * completed message) share the same shape — the only difference is where the data
 * comes from (already in hand for live, fetched on click for historical).
 */
const STATUS_ICON = {
  running: <Loader2 size={13} className="animate-spin text-blue-500" />,
  ok: <CheckCircle2 size={13} className="text-emerald-500" />,
  error: <XCircle size={13} className="text-red-500" />,
  denied: <ShieldOff size={13} className="text-amber-500" />,
};

function traceTiming(trace) {
  if (trace.status === 'running') return 'running…';
  if (trace.duration_ms != null) return `${trace.duration_ms}ms`;
  return '';
}

function TraceRow({ trace }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = trace.tool_input !== undefined || trace.tool_output !== undefined;
  return (
    <div className="border-b border-[var(--app-border)] last:border-0 py-2">
      <button
        type="button"
        className="w-full flex items-center gap-2 text-sm text-left"
        onClick={() => hasDetail && setExpanded((e) => !e)}
      >
        {STATUS_ICON[trace.status] || STATUS_ICON.ok}
        <span className="font-mono">{trace.tool_name}</span>
        <span className="text-xs text-secondary-500 ml-auto">{traceTiming(trace)}</span>
      </button>
      {expanded && (
        <div className="mt-2 space-y-1 pl-5">
          {trace.tool_input !== undefined && (
            <div>
              <div className="text-xs text-secondary-500">Input</div>
              <pre className="text-xs bg-[var(--app-border)]/20 rounded p-2 overflow-x-auto">{JSON.stringify(trace.tool_input, null, 2)}</pre>
            </div>
          )}
          {trace.tool_output !== undefined && (
            <div>
              <div className="text-xs text-secondary-500">Output</div>
              <pre className="text-xs bg-[var(--app-border)]/20 rounded p-2 overflow-x-auto">{JSON.stringify(trace.tool_output, null, 2)}</pre>
            </div>
          )}
          {trace.error && <div className="text-xs text-red-600">{trace.error}</div>}
        </div>
      )}
    </div>
  );
}

function TraceModal({ traces, belt, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[var(--app-surface)] rounded-lg shadow-xl w-full max-w-md max-h-[70vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--app-border)]">
          <span className="font-semibold text-sm">Tool execution stack</span>
          <button onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-gray-100"><X size={16} /></button>
        </div>
        {belt && (
          <p className="px-4 pt-2 text-xs text-secondary-500">
            Bound {belt.bound} of {belt.armed} tools for this turn (semantic selection).
          </p>
        )}
        <div className="overflow-y-auto px-4 py-1">
          {traces.length === 0 ? (
            <p className="text-sm text-secondary-500 py-3">No tool calls for this turn.</p>
          ) : (
            traces.map((t) => <TraceRow key={t.id || t.seq} trace={t} />)
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * `liveTraces` — pass while the turn is still in-flight (pendingJob.traces from the
 * status poll); the badge shows the currently-running tool and opens with the data
 * already in hand. Otherwise (a settled message), traces are fetched on click via
 * `fetchTraces({ sessionId, messageId })`.
 *
 * Transport is injected rather than imported, the same way Composer takes
 * onUploadAttachment and useCharacterSpeech takes fetchStatus/synthesize: the internal chat
 * reaches its traces through the authenticated fetchApi wrapper, and the public embed widget
 * — which has no cookies at all — through a key-scoped plain fetch that returns a REDACTED
 * payload (names/status/timing, never tool input or output). This component must not know
 * which of those it is rendering.
 */
export default function ToolTraceStack({
  sessionId, messageId, count, liveTraces, liveBelt, fetchTraces,
}) {
  const [open, setOpen] = useState(false);
  const [traces, setTraces] = useState(liveTraces || null);
  const [belt, setBelt] = useState(liveBelt || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (liveTraces) setTraces(liveTraces);
  }, [liveTraces]);

  useEffect(() => {
    if (liveBelt) setBelt(liveBelt);
  }, [liveBelt]);

  const isLive = !!liveTraces;
  if (!isLive && (!count || !fetchTraces)) return null;

  const openModal = async () => {
    setOpen(true);
    if (isLive || traces) return;
    setLoading(true);
    try {
      const data = await fetchTraces({ sessionId, messageId });
      setTraces(data.traces);
      setBelt(data.belt || null);
    } catch {
      setTraces([]);
    } finally {
      setLoading(false);
    }
  };

  const runningTool = isLive ? liveTraces.find((t) => t.status === 'running') : null;

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-[var(--app-border)] text-secondary-500 hover:bg-[var(--app-border)]/20"
      >
        {isLive ? (
          <>
            <Loader2 size={11} className="animate-spin" />
            {runningTool ? `running: ${runningTool.tool_name}` : 'working…'}
          </>
        ) : (
          <>
            <Wrench size={11} />
            {count} tool{count === 1 ? '' : 's'}
          </>
        )}
      </button>
      {open && !loading && traces && <TraceModal traces={traces} belt={belt} onClose={() => setOpen(false)} />}
    </>
  );
}
